import React, { useEffect, useId, useRef } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from './IconButton';

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleKey?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  side?: DrawerSide;
  size?: DrawerSize;
  closeOnOverlayClick?: boolean;
  closeOnEscapeKey?: boolean;
  hideCloseButton?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  showHandle?: boolean;
  panelClassName?: string;
  overlayClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  id?: string;
  onPanelMount?: (element: HTMLDivElement | null) => void;
}

const sideClasses: Record<DrawerSide, { position: string; border: string; radius: string; motion: { x?: string; y?: string } }> = {
  left: {
    position: 'left-0 top-0 h-full',
    border: 'border-r-2 border-black [.theme-liquid-glass_&]:border-r [.theme-liquid-glass_&]:border-white/20',
    radius: 'rounded-r-2xl [.theme-liquid-glass_&]:rounded-r-3xl [.theme-liquid-glass_&]:rounded-l-none',
    motion: { x: '-100%' },
  },
  right: {
    position: 'right-0 top-0 h-full',
    border: 'border-l-2 border-black [.theme-liquid-glass_&]:border-l [.theme-liquid-glass_&]:border-white/20',
    radius: 'rounded-l-2xl [.theme-liquid-glass_&]:rounded-l-3xl [.theme-liquid-glass_&]:rounded-r-none',
    motion: { x: '100%' },
  },
  top: {
    position: 'left-0 top-0 w-full',
    border: 'border-b-2 border-black [.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20',
    radius: 'rounded-b-2xl [.theme-liquid-glass_&]:rounded-b-3xl [.theme-liquid-glass_&]:rounded-t-none',
    motion: { y: '-100%' },
  },
  bottom: {
    position: 'left-0 bottom-0 w-full',
    border: 'border-t-2 border-black [.theme-liquid-glass_&]:border-t [.theme-liquid-glass_&]:border-white/20',
    radius: 'rounded-t-2xl [.theme-liquid-glass_&]:rounded-t-3xl [.theme-liquid-glass_&]:rounded-b-none',
    motion: { y: '100%' },
  },
};

const widthSizeClasses: Record<DrawerSize, string> = {
  sm: 'w-full max-w-sm',
  md: 'w-full max-w-md',
  lg: 'w-full max-w-lg',
  xl: 'w-full max-w-xl',
  full: 'w-full max-w-full',
};

const heightSizeClasses: Record<DrawerSize, string> = {
  sm: 'h-[32vh] min-h-[16rem]',
  md: 'h-[42vh] min-h-[20rem]',
  lg: 'h-[56vh] min-h-[24rem]',
  xl: 'h-[72vh] min-h-[28rem]',
  full: 'h-full',
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  titleKey,
  children,
  footer,
  side = 'right',
  size = 'lg',
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  hideCloseButton = false,
  initialFocusRef,
  showHandle = side === 'bottom',
  panelClassName,
  overlayClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  id,
  onPanelMount,
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const drawerId = id || autoId;
  const titleId = `${drawerId}-title`;
  const panelRef = useRef<HTMLDivElement>(null);

  const resolvedTitle = titleKey ? t(titleKey) : title;
  const sideConfig = sideClasses[side];
  const sizeClass = side === 'left' || side === 'right' ? widthSizeClasses[size] : heightSizeClasses[size];

  useEffect(() => {
    onPanelMount?.(isOpen ? panelRef.current : null);
  }, [isOpen, onPanelMount]);

  useEffect(() => {
    if (!isOpen || !closeOnEscapeKey) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscapeKey, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const focusTimeout = window.setTimeout(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
        return;
      }

      if (!panelRef.current) return;
      const closeButton = panelRef.current.querySelector('button[aria-label="Close drawer"]') as HTMLElement | null;
      const firstFocusable = panelRef.current.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
      );
      (closeButton && !hideCloseButton ? closeButton : firstFocusable ?? panelRef.current).focus();
    }, 100);

    const focusableElements = panelRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements && focusableElements.length > 0) {
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const trapFocus = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      };

      const currentPanel = panelRef.current;
      currentPanel?.addEventListener('keydown', trapFocus);
      return () => {
        window.clearTimeout(focusTimeout);
        currentPanel?.removeEventListener('keydown', trapFocus);
      };
    }

    return () => {
      window.clearTimeout(focusTimeout);
    };
  }, [hideCloseButton, initialFocusRef, isOpen]);

  if (typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000]" role="presentation">
          <motion.div
            data-slot="drawer-overlay"
            className={clsx(
              'absolute inset-0 bg-black/50',
              '[.theme-liquid-glass_&]:bg-slate-900/60 [.theme-liquid-glass_&]:backdrop-blur-sm',
              overlayClassName
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={closeOnOverlayClick ? onClose : undefined}
          />

          <motion.div
            ref={panelRef}
            data-slot="drawer-panel"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={resolvedTitle ? titleId : undefined}
            className={clsx(
              'absolute flex flex-col bg-white shadow-2xl focus:outline-none',
              '[.theme-liquid-glass_&]:bg-slate-800/80 [.theme-liquid-glass_&]:backdrop-blur-2xl',
              '[.theme-liquid-glass_&]:shadow-[0_24px_64px_rgba(0,0,0,0.4)]',
              sideConfig.position,
              sideConfig.border,
              sideConfig.radius,
              sizeClass,
              panelClassName
            )}
            initial={{ opacity: 1, ...sideConfig.motion }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 1, ...sideConfig.motion }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
          >
            {showHandle && (
              <div className="flex justify-center pt-3">
                <div className="h-1.5 w-10 rounded-full bg-slate-300 [.theme-liquid-glass_&]:bg-white/30" />
              </div>
            )}

            {(resolvedTitle || !hideCloseButton) && (
              <div
                data-slot="drawer-header"
                className={clsx(
                  'flex items-center justify-between p-4 sm:p-5',
                  side === 'bottom' || side === 'top'
                    ? 'border-b-2 border-black [.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20'
                    : 'border-b-2 border-black [.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20',
                  headerClassName
                )}
              >
                {resolvedTitle ? (
                  <h2 id={titleId} className="text-lg font-bold text-gray-900 [.theme-liquid-glass_&]:text-white">
                    {resolvedTitle}
                  </h2>
                ) : <div />}
                {!hideCloseButton && (
                  <IconButton
                    icon={<X size={20} />}
                    onClick={onClose}
                    ariaLabel="Close drawer"
                    variant="ghost"
                    size="sm"
                    className="ml-auto -mr-2"
                    applyNeobrutalistEffects={false}
                  />
                )}
              </div>
            )}

            <div data-slot="drawer-body" className={clsx('flex-grow overflow-y-auto p-4 sm:p-6', bodyClassName)}>
              {children}
            </div>

            {footer && (
              <div
                data-slot="drawer-footer"
                className={clsx(
                  'flex items-center justify-end gap-3 p-4 sm:p-5 border-t-2 border-black',
                  '[.theme-liquid-glass_&]:border-t [.theme-liquid-glass_&]:border-white/20',
                  footerClassName
                )}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export type SheetProps = DrawerProps;
export const Sheet = Drawer;
